// Zahlungen und Rechnungssumme einer Bestellung (Geld-Pfad): dieselben Formulare im Reiter
// Zahlungen der Akte und im Arbeitsplatz (21.10.2026 „alles in der Maske“). Der Server prueft
// Rechte und Betraege selbst; hier wird nur gelesen, was der Nutzer eingetippt hat.
import { useRef, useState, type ReactNode } from "react";
import { toast } from "sonner";
import { Info, Pencil, Plus, Trash2 } from "lucide-react";
import type { PurchasePayment, PurchaseShipment } from "@/api/purchase";
import {
  useAddPaymentAllocation, useAddPurchasePayment, useDeletePaymentAllocation, usePatchPurchaseOrder, usePatchPurchasePayment,
} from "@/api/purchase";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { formatUsd } from "@/lib/purchase-format";
import { formatEuroGenau } from "./board/InlineZellen";
import { parseBetrag } from "./board/zeilen";

/** Toast-Melder der Akte: `err("Rueckfall")(fehler)`. */
export type FehlerMelder = (rueckfall: string) => (e: unknown) => void;

export const ZAHLUNG_TYPEN = [["anzahlung", "Anzahlung"], ["balance", "Restzahlung"], ["neben", "Nebenkosten"]] as const;
export const ZAHLUNG_TYP_TEXT: Record<string, string> = Object.fromEntries(ZAHLUNG_TYPEN);

export const EUR_HILFE_TEXT =
  "EUR ist die tatsächliche Kontobelastung inklusive aller Bankkosten (Überweisungsgebühr und Kursaufschlag). " +
  "Diesen Betrag nicht noch einmal als Nebenkosten erfassen — er ist hier bereits vollständig enthalten.";

const BETRAG_UNLESBAR = "Betrag nicht lesbar (z. B. 1.234,50)";

const fmtDate = (d: string | null) =>
  d ? new Date(d).toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric", timeZone: "Europe/Berlin" }) : "—";

function EurHilfe() {
  return (
    <details className="ml-1 inline-block align-middle font-normal">
      <summary
        title={EUR_HILFE_TEXT}
        aria-label="Erklärung zur tatsächlichen Kontobelastung"
        className="inline-flex cursor-help list-none items-center text-ink-2 hover:text-ink [&::-webkit-details-marker]:hidden"
      >
        <Info className="size-3.5" aria-hidden="true" />
      </summary>
      <p className="mt-1 max-w-[48ch] rounded border border-line bg-panel p-2 text-[11px] leading-snug text-ink-2">
        {EUR_HILFE_TEXT}
      </p>
    </details>
  );
}

function Zahlungsfeld({ label, wert, hilfe }: { label: string; wert: string; hilfe?: ReactNode }) {
  return (
    <div className="min-w-0">
      <dt className="flex items-start gap-1 text-[11px] font-medium text-ink-2">
        <span>{label}</span>{hilfe}
      </dt>
      <dd className="break-words text-xs text-ink">{wert}</dd>
    </div>
  );
}

function kursText(zahlung: PurchasePayment): string {
  // Legacy `kurs` hat die Gegenrichtung USD/EUR und darf nie als EUR je USD erscheinen.
  const kurs = zahlung.kurs_eur_pro_usd ?? null;
  if (kurs === null) return "—";
  return `${kurs.toLocaleString("de-DE", { minimumFractionDigits: 4, maximumFractionDigits: 4 })} EUR je USD inkl. Bankkosten`;
}

const alsBetragsfeld = (v: number | null) => (v === null ? "" : String(v).replace(".", ","));

function alsZahlungsEntwurf(zahlung: PurchasePayment) {
  return {
    amount_usd: alsBetragsfeld(zahlung.amount_usd),
    amount_eur: alsBetragsfeld(zahlung.amount_eur),
    paid_at: zahlung.paid_at ? zahlung.paid_at.slice(0, 10) : "",
    beleg_ref: zahlung.beleg_ref ?? "",
    note: zahlung.note ?? "",
  };
}

/**
 * Erfasste Zahlung korrigieren. Gesendet wird nur, was sich geaendert hat; der `typ` ist
 * serverseitig gesperrt und hat deshalb kein Feld. `kompakt` = schmale Spalte des Arbeitsplatzes.
 */
export function ZahlungBearbeiten({ zahlung, err, onFertig, kompakt = false }: {
  zahlung: PurchasePayment;
  err: FehlerMelder;
  onFertig: () => void;
  kompakt?: boolean;
}) {
  const patch = usePatchPurchasePayment();
  // Stand beim Oeffnen: Grundlage des Vergleichs, auch wenn die Akte inzwischen neu laedt.
  const [basis] = useState(() => alsZahlungsEntwurf(zahlung));
  const [entwurf, setEntwurf] = useState(basis);
  const [meldung, setMeldung] = useState<string | null>(null);

  function speichern() {
    const usd = parseBetrag(entwurf.amount_usd);
    const eur = parseBetrag(entwurf.amount_eur);
    if (usd === undefined || eur === undefined) {
      setMeldung(BETRAG_UNLESBAR);
      return;
    }
    if (usd === null && eur === null) {
      setMeldung("Mindestens einen Betrag eintragen");
      return;
    }

    const paidAt = entwurf.paid_at === "" ? null : entwurf.paid_at;
    const beleg = entwurf.beleg_ref.trim() === "" ? null : entwurf.beleg_ref.trim();
    const notiz = entwurf.note.trim() === "" ? null : entwurf.note.trim();
    const body: Record<string, unknown> = {};
    if (entwurf.amount_usd !== basis.amount_usd) body.amount_usd = usd;
    if (entwurf.amount_eur !== basis.amount_eur) body.amount_eur = eur;
    if (entwurf.paid_at !== basis.paid_at) body.paid_at = paidAt;
    if (entwurf.beleg_ref !== basis.beleg_ref) body.beleg_ref = beleg;
    if (entwurf.note !== basis.note) body.note = notiz;
    if (Object.keys(body).length === 0) {
      onFertig();
      return;
    }

    setMeldung(null);
    patch.mutate(
      { id: zahlung.id, body },
      {
        onSuccess: () => { onFertig(); toast.success("Zahlung gespeichert"); },
        onError: err("Zahlung konnte nicht gespeichert werden"),
      },
    );
  }

  const raster = kompakt ? "grid grid-cols-2 gap-2" : "grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3";
  const breit = kompakt ? "col-span-2" : "";
  return (
    <div className="mt-2 space-y-2">
      <div className={raster}>
        <label className={`text-[11px] text-ink-2 ${breit}`}>
          Bezahlter Rechnungsbetrag (USD)
          <Input inputMode="decimal" value={entwurf.amount_usd}
            onChange={(e) => setEntwurf((d) => ({ ...d, amount_usd: e.target.value }))}
            className="mt-1 h-8 w-full text-xs" />
        </label>
        <label className={`text-[11px] text-ink-2 ${breit}`}>
          Tatsächliche Kontobelastung (EUR inkl. Bankkosten)
          <Input inputMode="decimal" value={entwurf.amount_eur}
            onChange={(e) => setEntwurf((d) => ({ ...d, amount_eur: e.target.value }))}
            className="mt-1 h-8 w-full text-xs" />
        </label>
        <label className="text-[11px] text-ink-2">
          Bezahlt am
          <Input type="date" value={entwurf.paid_at}
            onChange={(e) => setEntwurf((d) => ({ ...d, paid_at: e.target.value }))}
            className="mt-1 h-8 w-full text-xs" />
        </label>
        <label className="text-[11px] text-ink-2">
          Beleg
          <Input value={entwurf.beleg_ref}
            onChange={(e) => setEntwurf((d) => ({ ...d, beleg_ref: e.target.value }))}
            className="mt-1 h-8 w-full text-xs" />
        </label>
        <label className={`text-[11px] text-ink-2 ${kompakt ? "col-span-2" : "sm:col-span-2"}`}>
          Notiz
          <Input value={entwurf.note}
            onChange={(e) => setEntwurf((d) => ({ ...d, note: e.target.value }))}
            className="mt-1 h-8 w-full text-xs" />
        </label>
      </div>
      <p className="text-[11px] text-ink-2">{EUR_HILFE_TEXT}</p>
      <div className="flex flex-wrap items-center gap-2">
        <Button size="sm" className="h-7 px-3 text-xs" disabled={patch.isPending} onClick={speichern}>
          Speichern
        </Button>
        <Button size="sm" variant="ghost" className="h-7 px-3 text-xs" disabled={patch.isPending} onClick={onFertig}>
          Abbrechen
        </Button>
        {meldung ? <span role="alert" className="text-xs text-coral">{meldung}</span> : null}
      </div>
    </div>
  );
}

/** Karte einer Zahlung im Reiter Zahlungen: Beschriftete Werte, Bearbeiten und Loeschen. */
export function ZahlungsKarte({
  zahlung,
  canEdit,
  onLoeschen,
  err,
}: {
  zahlung: PurchasePayment;
  canEdit: boolean;
  onLoeschen: () => void;
  err: FehlerMelder;
}) {
  const [offen, setOffen] = useState(false);

  return (
    <div>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="flex items-center gap-2">
          <span className="rounded bg-background px-1.5 py-0.5 text-[11px] font-medium text-ink">{zahlung.typ}</span>
          <span className="text-[11px] text-ink-2">Zahlungsart (nur Anzeige)</span>
        </div>
        {canEdit && !offen && (
          <div className="flex items-center gap-1">
            <Button
              variant="outline" size="sm" className="h-7 gap-1 px-2 text-xs"
              aria-label={`Zahlung bearbeiten ${zahlung.id}`}
              onClick={() => setOffen(true)}
            >
              <Pencil className="size-3.5" /> Bearbeiten
            </Button>
            <Button
              variant="ghost" size="sm" className="size-7 p-0"
              aria-label={`Zahlung löschen ${zahlung.id}`}
              onClick={onLoeschen}
            >
              <Trash2 className="size-3.5 text-coral" />
            </Button>
          </div>
        )}
      </div>

      {canEdit && offen ? (
        <ZahlungBearbeiten zahlung={zahlung} err={err} onFertig={() => setOffen(false)} />
      ) : (
        <dl className="mt-2 grid grid-cols-1 gap-x-4 gap-y-2 sm:grid-cols-2 lg:grid-cols-3">
          <Zahlungsfeld label="Bezahlter Rechnungsbetrag (USD)" wert={formatUsd(zahlung.amount_usd)} />
          <Zahlungsfeld label="Tatsächliche Kontobelastung (EUR inkl. Bankkosten)"
            wert={formatEuroGenau(zahlung.amount_eur)} hilfe={<EurHilfe />} />
          <Zahlungsfeld label="Effektiver Belastungskurs" wert={kursText(zahlung)} />
          <Zahlungsfeld label="Bezahlt am" wert={fmtDate(zahlung.paid_at)} />
          <Zahlungsfeld label="Beleg" wert={zahlung.beleg_ref ?? "—"} />
          {zahlung.note ? <Zahlungsfeld label="Notiz" wert={zahlung.note} /> : null}
        </dl>
      )}
    </div>
  );
}

const LEERE_ZAHLUNG = { typ: "anzahlung", amount_usd: "", amount_eur: "", paid_at: "", beleg_ref: "" };

/**
 * Neue Zahlung erfassen (POST wie bisher im Reiter Zahlungen). Ein Riegel im Handler haelt
 * einen Doppelklick ab, bevor `disabled` greift — eine doppelte Zahlung verfaelscht Saldo und Kurs.
 */
export function NeueZahlung({ orderId, err, kompakt = false, onAbbrechen, onGespeichert }: {
  orderId: number;
  err: FehlerMelder;
  kompakt?: boolean;
  /** Nur kompakt: Formular wieder zuklappen. */
  onAbbrechen?: () => void;
  onGespeichert?: () => void;
}) {
  const add = useAddPurchasePayment(orderId);
  const [entwurf, setEntwurf] = useState(LEERE_ZAHLUNG);
  const [meldung, setMeldung] = useState<string | null>(null);
  const laeuft = useRef(false);

  function speichern() {
    if (laeuft.current) return;
    const usd = parseBetrag(entwurf.amount_usd);
    const eur = parseBetrag(entwurf.amount_eur);
    if (usd === undefined || eur === undefined) return void setMeldung(BETRAG_UNLESBAR);
    if (usd === null && eur === null) return void setMeldung("Mindestens einen Betrag eintragen");
    setMeldung(null);
    laeuft.current = true;
    add.mutate(
      { typ: entwurf.typ, amount_usd: usd, amount_eur: eur, paid_at: entwurf.paid_at || null, beleg_ref: entwurf.beleg_ref || null },
      {
        onSuccess: () => { setEntwurf(LEERE_ZAHLUNG); toast.success("Zahlung erfasst"); onGespeichert?.(); },
        onError: err("Zahlung fehlgeschlagen"),
        onSettled: () => { laeuft.current = false; },
      },
    );
  }

  const raster = kompakt ? "grid grid-cols-2 gap-2" : "mt-2 grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-5";
  const breit = kompakt ? "col-span-2" : "";
  const felder = (
    <div className={raster}>
      <label className={`text-[11px] text-ink-2 ${breit}`}>
        Zahlungsart
        <select
          value={entwurf.typ}
          data-fokus-ziel="zahlung"
          onChange={(e) => setEntwurf((d) => ({ ...d, typ: e.target.value }))}
          className="mt-1 h-8 w-full rounded border border-line bg-background px-2 text-xs"
          aria-label="Zahlungs-Typ"
        >
          {ZAHLUNG_TYPEN.map(([wert, text]) => <option key={wert} value={wert}>{text}</option>)}
        </select>
      </label>
      <label className={`text-[11px] text-ink-2 ${breit}`}>
        Bezahlter Rechnungsbetrag (USD)
        <Input inputMode="decimal" value={entwurf.amount_usd}
          onChange={(e) => setEntwurf((d) => ({ ...d, amount_usd: e.target.value }))}
          className="mt-1 h-8 w-full text-xs" />
      </label>
      <label className={`text-[11px] text-ink-2 ${breit}`}>
        Tatsächliche Kontobelastung (EUR inkl. Bankkosten)
        <Input inputMode="decimal" value={entwurf.amount_eur}
          onChange={(e) => setEntwurf((d) => ({ ...d, amount_eur: e.target.value }))}
          className="mt-1 h-8 w-full text-xs" />
      </label>
      <label className="text-[11px] text-ink-2">
        Bezahlt am
        <Input type="date" value={entwurf.paid_at}
          onChange={(e) => setEntwurf((d) => ({ ...d, paid_at: e.target.value }))}
          className="mt-1 h-8 w-full text-xs" />
      </label>
      <label className="text-[11px] text-ink-2">
        Beleg
        <Input value={entwurf.beleg_ref}
          onChange={(e) => setEntwurf((d) => ({ ...d, beleg_ref: e.target.value }))}
          className="mt-1 h-8 w-full text-xs" />
      </label>
    </div>
  );
  const knoepfe = (
    <div className="mt-2 flex flex-wrap items-center gap-2">
      <Button size="sm" className="gap-1.5" disabled={add.isPending} onClick={speichern}>
        <Plus className="size-3.5" /> Zahlung speichern
      </Button>
      {onAbbrechen ? (
        <Button size="sm" variant="ghost" disabled={add.isPending} onClick={onAbbrechen}>Abbrechen</Button>
      ) : null}
      {meldung ? <span role="alert" className="text-xs text-coral">{meldung}</span> : null}
    </div>
  );

  if (kompakt) {
    return (
      <div className="mt-2.5 rounded-md border border-line bg-panel px-2.5 py-2">
        {felder}
        <p className="mt-2 text-[11px] leading-snug text-ink-2">EUR = Kontobelastung inkl. Bankkosten; nicht zusätzlich als Nebenkosten erfassen.</p>
        {knoepfe}
      </div>
    );
  }
  return (
    <div className="mt-4 rounded border border-line bg-panel-2 p-3">
      <p className="text-xs font-medium text-ink">Neue Zahlung erfassen</p>
      {felder}
      <p className="mt-2 text-[11px] text-ink-2">{EUR_HILFE_TEXT}</p>
      {knoepfe}
    </div>
  );
}

/** Rechnungssumme (Invoice) der Bestellung in USD; leer lassen heisst: der Wert bleibt. */
export function RechnungssummeFeld({ orderId, invoice, err, label = "Rechnungssumme (USD)", knopf = "Speichern", kompakt = false }: {
  orderId: number;
  invoice: number | null;
  err: FehlerMelder;
  label?: string;
  knopf?: string;
  kompakt?: boolean;
}) {
  const patch = usePatchPurchaseOrder(orderId);
  // null = unberuehrt, das Feld zeigt den gespeicherten Wert; "" = bewusst geleert.
  const [entwurf, setEntwurf] = useState<string | null>(null);
  const [meldung, setMeldung] = useState<string | null>(null);

  function speichern() {
    if (entwurf === null) return;
    const v = parseBetrag(entwurf);
    if (v === undefined || (v !== null && v < 0)) return void setMeldung(BETRAG_UNLESBAR);
    // Leer heisst: eine gespeicherte Summe wieder entfernen (PATCH null). Ohne gespeicherte Summe gibt es nichts zu leeren.
    if (v === null && invoice === null) return void setMeldung("Summe eintragen");
    setMeldung(null);
    patch.mutate({ invoice_total_usd: v }, {
      onSuccess: () => { setEntwurf(null); toast.success(v === null ? "Rechnungssumme entfernt" : "Rechnungssumme gespeichert"); },
      onError: err("Speichern fehlgeschlagen"),
    });
  }

  return (
    <div className={kompakt ? "" : "mb-3"}>
      <div className="flex items-end gap-2">
        <label className={`text-xs text-ink-2 ${kompakt ? "min-w-0 flex-1" : ""}`}>
          {label}
          <Input
            inputMode="decimal"
            data-fokus-ziel="invoice"
            value={entwurf ?? alsBetragsfeld(invoice)}
            onChange={(e) => setEntwurf(e.target.value)}
            className={`mt-1 h-8 text-xs ${kompakt ? "w-full" : "w-32"}`}
          />
        </label>
        <Button size="sm" variant="outline" disabled={patch.isPending || entwurf === null} onClick={speichern}>
          {knopf}
        </Button>
      </div>
      {meldung ? <p role="alert" className="mt-1 text-xs text-coral">{meldung}</p> : null}
    </div>
  );
}

/**
 * Anrechnung einer Zahlung auf Container (E4: manuell) — Reiter Zahlungen und Saldo im
 * Arbeitsplatz. Die Deckung prueft der Server (409 mit Klartext). Ein Riegel im Handler haelt
 * einen Doppelklick ab: eine doppelte Anrechnung verschiebt Geld zwischen Containern.
 */
export function ZahlungAnrechnung({ zahlung, shipments, titelVon, darf, err, formular = true, vorwahl = null, onFertig, kompakt = false }: {
  zahlung: PurchasePayment;
  shipments: PurchaseShipment[];
  titelVon: (shipmentId: number) => string;
  /** Anrechnen und Loeschen (article_purchase_prices). */
  darf: boolean;
  err: FehlerMelder;
  /** Formular zeigen; im Arbeitsplatz erst nach „Zahlung n anrechnen“. */
  formular?: boolean;
  /** Vorbelegter Container (Arbeitsplatz: ausgewaehlte Spur); sonst „Container wählen…“. */
  vorwahl?: number | null;
  /** Formular wieder zuklappen (nach Erfolg oder „Abbrechen“). */
  onFertig?: () => void;
  kompakt?: boolean;
}) {
  const add = useAddPaymentAllocation();
  const entfernen = useDeletePaymentAllocation();
  const [container, setContainer] = useState(vorwahl === null ? "" : String(vorwahl));
  const [betrag, setBetrag] = useState("");
  const [meldung, setMeldung] = useState<string | null>(null);
  const laeuft = useRef(false);
  const schrift = kompakt ? "text-xs" : "text-[11px]";
  const knopf = kompakt ? "h-8 px-2.5 text-xs" : "h-6 px-2 text-[11px]";

  function anrechnen() {
    if (laeuft.current) return;
    const shipmentId = Number(container);
    const amount = parseBetrag(betrag);
    if (!Number.isSafeInteger(shipmentId) || shipmentId <= 0) return void setMeldung("Container wählen");
    if (amount === undefined) return void setMeldung(BETRAG_UNLESBAR);
    if (amount === null || amount <= 0) return void setMeldung("Betrag größer 0 eintragen");
    setMeldung(null);
    laeuft.current = true;
    add.mutate(
      { paymentId: zahlung.id, body: { purchase_shipment_id: shipmentId, amount_usd: amount } },
      {
        onSuccess: () => {
          setBetrag("");
          setContainer(vorwahl === null ? "" : String(vorwahl));
          toast.success("Angerechnet");
          onFertig?.();
        },
        onError: err("Anrechnung fehlgeschlagen"),
        onSettled: () => { laeuft.current = false; },
      },
    );
  }

  return (
    <div className={kompakt ? "mt-1 space-y-1 pl-3" : "mt-2 space-y-1 pl-4"}>
      {zahlung.allocations.map((a) => (
        <div key={a.id} className={`flex min-w-0 flex-wrap items-center gap-x-2 ${schrift} text-ink-2`}>
          <span className="min-w-0 flex-1 truncate" title={`${titelVon(a.purchase_shipment_id)}: ${formatUsd(a.amount_usd)}`}>
            → {titelVon(a.purchase_shipment_id)}: {formatUsd(a.amount_usd)}
          </span>
          {darf ? (
            <Button
              type="button" variant="ghost" size="sm" className="size-5 p-0"
              aria-label={`Anrechnung löschen ${a.id}`}
              disabled={entfernen.isPending}
              onClick={() => entfernen.mutate(a.id, {
                onSuccess: () => toast.success("Anrechnung entfernt"),
                onError: err("Löschen fehlgeschlagen"),
              })}
            >
              <Trash2 className="size-3 text-coral" />
            </Button>
          ) : null}
        </div>
      ))}
      {darf && formular && shipments.length > 0 ? (
        <>
          <div className="flex flex-wrap items-center gap-2">
            <select
              value={container}
              data-fokus-ziel="anrechnung"
              onChange={(e) => setContainer(e.target.value)}
              className={`rounded border border-line bg-background px-1.5 py-0.5 ${schrift} ${kompakt ? "h-8 min-w-0 flex-1" : ""}`}
              aria-label={`Anrechnungs-Container Zahlung ${zahlung.id}`}
            >
              <option value="">Container wählen…</option>
              {shipments.map((sh) => <option key={sh.id} value={sh.id}>{titelVon(sh.id)}</option>)}
            </select>
            <Input
              placeholder="USD"
              inputMode="decimal"
              value={betrag}
              onChange={(e) => setBetrag(e.target.value)}
              className={kompakt ? "h-8 w-24 text-xs" : "h-6 w-20 text-[11px]"}
            />
            <Button type="button" variant="outline" size="sm" className={knopf} disabled={add.isPending} onClick={anrechnen}>
              Anrechnen
            </Button>
            {onFertig ? (
              <Button type="button" variant="ghost" size="sm" className={knopf} disabled={add.isPending} onClick={onFertig}>
                Abbrechen
              </Button>
            ) : null}
          </div>
          {meldung ? <p role="alert" className="text-xs text-coral">{meldung}</p> : null}
        </>
      ) : null}
    </div>
  );
}
